import * as React from 'react'
import classNames from 'classnames'
import type { DayModifiers } from 'react-day-picker'
import DayPicker from 'react-day-picker'
import defaultClassNames from 'react-day-picker/lib/src/classNames'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'

import {
  getPacificTimezone,
  firstWODDate,
} from 'ui-component-library/utils/helpers'

import Calendar from 'ui-component-library/icons/calendar.svg'

import type { YearMonthProps } from './YearMonthForm'

import YearMonthForm from './YearMonthForm'

import 'react-day-picker/lib/style.css'
import dayPickerStyles from './dayPickerStyles.scss'
import style from './style.scss'

export type DatePickerProps = YearMonthProps & {
  buttonLabelText: string
  navLabelText: string
  todayButtonText: string
}

type Props = DatePickerProps & {
  className?: string
  handleDayClick: (date: Date) => void
}

type State = {
  focusDate: Date
  isOpen: boolean
  todayDate: Date
}

class DatePicker extends React.PureComponent<Props, State> {
  static defaultProps = {
    className: '',
  }

  constructor(props: Props) {
    super(props)

    const currentDate = new Date()
    this.state = {
      isOpen: false,
      focusDate: currentDate,
      todayDate: currentDate,
    }
  }

  dayPickerContainer: { current: HTMLElement | null } = React.createRef()

  yearMonthFormId = 'year-month-form'

  prevDate: Date = getPacificTimezone()

  componentDidUpdate(prevProp: Props, prevState: State) {
    if (prevState.isOpen && !this.state.isOpen) {
      document.removeEventListener('click', this.handleCloseListener)
    }
    if (!prevState.isOpen && this.state.isOpen) {
      document.addEventListener('click', this.handleCloseListener)
      this.prevDate = getPacificTimezone()
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleCloseListener)
  }

  handleCloseListener = (event: MouseEvent) => {
    const { current } = this.dayPickerContainer
    if (current && event.target) {
      const target = event.target as Element
      const isInsideYearMonthForm = target.closest(`#${this.yearMonthFormId}`) // Fixes issues related to selector failure for selectors outside of YearMonthForm
      const isInsideDatePicker = current.contains(event.target as Node)
      if (!isInsideDatePicker && !isInsideYearMonthForm) {
        if (this.state.isOpen) {
          this.handleToggle()
        }
      }
    }
  }

  handleToggle = () => this.setState((state) => ({ isOpen: !state.isOpen }))

  handleDisabledDays = (day: Date) => {
    if (day.getTime() < firstWODDate.getTime()) {
      return true
    }

    if (this.prevDate.getHours() > 15) {
      const tomorrow = new Date(this.prevDate)
      tomorrow.setDate(this.prevDate.getDate() + 1)

      return day.getTime() > tomorrow.getTime()
    }
    return differenceInCalendarDays(day, this.prevDate) > 0
  }

  handleYearMonthChange = (focusDate: Date) => this.setState({ focusDate })

  handleDayClick = (date: Date, modifiers: DayModifiers) => {
    const { handleDayClick } = this.props

    if (modifiers[dayPickerStyles.disabled]) {
      return
    }
    this.setState({ focusDate: date })
    handleDayClick(date)
  }

  render() {
    const {
      buttonLabelText,
      className,
      monthText,
      navLabelText,
      showNextMonthLabelText,
      showPreviousMonthLabelText,
      todayButtonText,
      yearText,
    } = this.props
    const { isOpen, focusDate, todayDate } = this.state

    return (
      <nav
        ref={this.dayPickerContainer}
        aria-label={navLabelText}
        className={className}
      >
        <button
          aria-expanded={isOpen}
          aria-label={buttonLabelText}
          className={classNames(style.calendarButton, {
            [style.active]: isOpen,
          })}
          onClick={this.handleToggle}
        >
          <Calendar className={classNames(style.calendar, style.light)} />
        </button>
        <div
          className={classNames(style.calendarContainer, {
            [style.hidden]: !isOpen,
          })}
        >
          <DayPicker
            captionElement={() => null}
            classNames={{
              ...defaultClassNames,
              ...dayPickerStyles,
            }}
            modifiers={{
              [dayPickerStyles.disabled]: this.handleDisabledDays,
            }}
            month={focusDate}
            navbarElement={({ month, previousMonth, nextMonth }) => (
              <YearMonthForm
                date={month}
                monthText={monthText}
                nextMonth={nextMonth}
                previousMonth={previousMonth}
                showNextMonthLabelText={showNextMonthLabelText}
                showPreviousMonthLabelText={showPreviousMonthLabelText}
                todayDate={todayDate}
                yearMonthFormId={this.yearMonthFormId}
                yearText={yearText}
                onChange={this.handleYearMonthChange}
              />
            )}
            selectedDays={focusDate}
            todayButton={todayButtonText}
            onDayClick={this.handleDayClick}
          />
        </div>
      </nav>
    )
  }
}

export default DatePicker
